import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { Form, Skeleton } from 'antd';

import Image from 'global/Image';
import If from 'global/Common/If';

import useMultiPortfolioStore from 'lib/stores/multiPorto';
import usePortfolioSelect from 'features/multi-porto/hooks/usePortfolioSelect';
import { lotSize } from 'global/TradingModal/constant';
import { lotFormatter } from 'utils/securities/formatter';

import CompanyIcon from 'features/multi-porto/components/CompanyLogo';
import InputStepper from 'global/Securities/components/InputStepper';
import BaseSecuritiesModal from 'global/Securities/components/Modal/BaseSecuritiesModal';
import type { BaseSecuritiesModalProps } from 'global/Securities/components/Modal/BaseSecuritiesModal';
import { iStocks } from 'global/Securities/types/api/multiPorto';

import PortfolioSelect from '../TransferPortfolioSelect';

import {
  Container,
  ScrollableContainer,
  ButtonWrapper,
  NextButton,
  SelectContainer,
  SwitchButton,
  SectionTitle,
  StockListContainer,
  EmptyContainer,
  EmptyText,
  StockItem,
  StockItemInfoWrapper,
  StockItemInfo,
  StockItemInputWrapper,
} from './stock.styled';

interface StocksForm {
  [key: string]: iStocks;
}

const TransferStock = ({
  onCancel = () => {},
  onSubmit = () => {},
  ...props
}: BaseSecuritiesModalProps) => {
  const [form] = Form.useForm();
  const [stocks, setStocks] = useState<StocksForm>({});

  const {
    fromPortfolio,
    fromPortfolioOptions,
    toPortfolio,
    toPortfolioOptions,
    disabledSelect,
    onChangeFromPortfolio,
    onChangeToPortfolio,
    onSwitchPortfolio,
    onReset,
  } = usePortfolioSelect({
    visible: props.visible,
    form,
  });

  const subAccountsLoading = useMultiPortfolioStore(
    (s) => s.subAccountsData.isLoading,
  );

  // stock transfer form
  const moveStockForm = useMultiPortfolioStore((s) => s.moveStockForm);
  const setMoveStockForm = useMultiPortfolioStore((s) => s.setMoveStockForm);
  const resetMoveStockForm = useMultiPortfolioStore(
    (s) => s.resetMoveStockForm,
  );

  // transferable stocks
  const transferableLoading = useMultiPortfolioStore(
    (s) => s.transferableStocks.isLoading,
  );
  const transferableStocksLists = useMultiPortfolioStore(
    (s) => s.transferableStocks.data.stocks,
  );
  const getTransferableStocks = useMultiPortfolioStore(
    (s) => s.getTransferableStocks,
  );
  const resetTransferableStocks = useMultiPortfolioStore(
    (s) => s.resetTransferableStocks,
  );

  const transferableStocksList = useMemo(
    () =>
      transferableStocksLists.filter(
        (stock) => Number(stock.transferable_shares_qty) >= 100,
      ) || [],
    [transferableStocksLists],
  );

  const disabledSubmit = useMemo(() => {
    const filledShares = Object.values(stocks).some(
      (stock) => stock.shares > 0,
    );

    return !fromPortfolio || !toPortfolio || !filledShares;
  }, [fromPortfolio, toPortfolio, stocks]);

  const showEmptyList = useMemo(
    () => !transferableStocksList.length || !fromPortfolio,
    [transferableStocksList, fromPortfolio],
  );

  const showShimmer = useMemo(
    () =>
      transferableLoading || (subAccountsLoading && !moveStockForm.from_acc_no),
    [transferableLoading, subAccountsLoading, moveStockForm.from_acc_no],
  );

  const handleResetStockFields = () => {
    // Get all field names
    const allFields = form.getFieldsValue();

    // Filter out field names starting with `stock-`
    const stockFieldNames = Object.keys(allFields).filter((fieldName) =>
      fieldName.startsWith('stock-'),
    );

    // Reset only those fields
    form.resetFields(stockFieldNames);
  };

  const handleLotChange = (value: number, symbol: string) => {
    setStocks({
      ...stocks,
      [symbol]: {
        ...stocks[symbol],
        shares: value * lotSize,
      },
    });
  };

  const handleReset = () => {
    onReset();
    setStocks({});
    resetTransferableStocks();
  };

  const handleSaveForm = () => {
    setMoveStockForm({
      from_acc_no: fromPortfolio || '',
      from_acc_name: fromPortfolioOptions.find(
        (subAccount) => subAccount.value === fromPortfolio,
      )?.label,
      to_acc_no: toPortfolio || '',
      to_acc_name: toPortfolioOptions.find(
        (subAccount) => subAccount.value === toPortfolio,
      )?.label,
      stocks_data: Object.values(stocks).filter((stock) => stock.shares > 0),
    });
  };

  const handleSubmit = () => {
    onSubmit();
    handleSaveForm();
  };

  const handleCancel = (e: MouseEvent<HTMLElement>) => {
    onCancel(e);
    handleReset();
    resetMoveStockForm();
  };

  // Set initial value for stocks
  useEffect(() => {
    setStocks(
      transferableStocksList.reduce((acc, stock) => {
        // eslint-disable-next-line no-param-reassign
        acc[stock.stock_code] = {
          stock_code: stock.stock_code,
          shares: 0,
          avatar_url: stock.stock_icon_url || '',
        };

        return acc;
      }, {}),
    );
  }, [transferableStocksList]);

  // Get transferable stocks when fromPortfolio is set
  useEffect(() => {
    if (fromPortfolio) {
      getTransferableStocks(fromPortfolio);
      handleResetStockFields();
    }
  }, [fromPortfolio, getTransferableStocks]);

  // Reset form when saved fromPortfolio is empty
  useEffect(() => {
    if (!moveStockForm.from_acc_no) handleReset();
  }, [moveStockForm.from_acc_no]);

  return (
    <BaseSecuritiesModal
      title="Transfer Stock"
      hideSubmit
      onCancel={handleCancel}
      className="modal-multi-porto"
      transitionName=""
      {...props}
    >
      <Container onClick={(e) => e.stopPropagation()}>
        <Form form={form} layout="vertical">
          <ScrollableContainer>
            <SelectContainer>
              <SwitchButton onClick={onSwitchPortfolio}>
                <Image src="/icon/multi-porto/transfer-switch.svg" alt="" />
              </SwitchButton>

              <PortfolioSelect
                label="From Portfolio"
                name="fromPortfolio"
                value={fromPortfolio}
                options={fromPortfolioOptions}
                onChange={onChangeFromPortfolio}
                disabled={disabledSelect}
                id="transfer-stock-source"
              />

              <PortfolioSelect
                label="To Portfolio"
                name="toPortfolio"
                value={toPortfolio}
                options={toPortfolioOptions}
                onChange={onChangeToPortfolio}
                disabled={disabledSelect}
                id="transfer-stock-destination"
              />
            </SelectContainer>

            <SectionTitle>
              <h1>Select Stocks</h1>
              <p>You can select stocks that have been settled (T+2).</p>
            </SectionTitle>

            <StockListContainer>
              <If condition={showShimmer}>
                {Array.from({ length: 2 }).map((_, idx) => (
                  <StockItem key={`empty-row-${idx.toString()}`}>
                    <StockItemInfoWrapper>
                      <Skeleton.Avatar active size={32} shape="circle" />

                      <StockItemInfo>
                        <Skeleton.Input
                          style={{ width: 50, height: 13 }}
                          active
                        />
                        <Skeleton.Input
                          style={{ width: 100, height: 13 }}
                          active
                        />
                      </StockItemInfo>
                    </StockItemInfoWrapper>

                    <StockItemInputWrapper>
                      <Skeleton.Input
                        style={{ width: 140, height: 32 }}
                        active
                      />
                    </StockItemInputWrapper>
                  </StockItem>
                ))}

                <If.Else>
                  <If condition={showEmptyList}>
                    <EmptyContainer>
                      <Image
                        src="/images/multi-porto/empty-stock.svg"
                        alt=""
                        width={100}
                        height={75}
                      />

                      <EmptyText>
                        <h1>Your Portfolio is Empty</h1>
                        <p>
                          You can transfer your stocks to this portfolio and
                          start investing.
                        </p>
                      </EmptyText>
                    </EmptyContainer>
                  </If>

                  <If condition={!showEmptyList}>
                    {transferableStocksList?.map((stock) => {
                      const {
                        stock_code,
                        transferable_shares_qty,
                        stock_icon_url,
                      } = stock;
                      const totalLot = Math.floor(
                        Number(transferable_shares_qty) / lotSize,
                      );

                      return (
                        <StockItem key={stock.stock_code}>
                          <StockItemInfoWrapper>
                            <CompanyIcon
                              symbol={stock.stock_code}
                              avatar_url={stock_icon_url}
                              size={32}
                            />

                            <StockItemInfo>
                              <h1>{stock_code}</h1>
                              <p>
                                Available Lot:{' '}
                                <span>{lotFormatter(totalLot)} Lot</span>
                              </p>
                            </StockItemInfo>
                          </StockItemInfoWrapper>

                          <StockItemInputWrapper>
                            <Form.Item
                              name={`stock-${stock.stock_code}`}
                              label=""
                              style={{ marginBottom: 0 }}
                            >
                              <InputStepper
                                min={0}
                                max={totalLot}
                                disableDecimal
                                onChange={(val) =>
                                  handleLotChange(val, stock_code)
                                }
                                containerClassName="input-stepper-container"
                              />
                            </Form.Item>
                          </StockItemInputWrapper>
                        </StockItem>
                      );
                    })}
                  </If>
                </If.Else>
              </If>
            </StockListContainer>
          </ScrollableContainer>

          <ButtonWrapper>
            <Form.Item style={{ margin: '0px' }}>
              <NextButton
                onClick={handleSubmit}
                type="primary"
                disabled={disabledSubmit}
              >
                Continue
              </NextButton>
            </Form.Item>
          </ButtonWrapper>
        </Form>
      </Container>
    </BaseSecuritiesModal>
  );
};

export default TransferStock;
